import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Shield, Shirt, ChevronRight, Star, Sparkles, PartyPopper, Crown, Heart, GraduationCap, Cake, Play, Gift, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import Header from "../components/Header";
import FamousBirthdays from "../components/FamousBirthdays";
import AnimatedLogo from "../components/AnimatedLogo";
import { API } from "../App";
import { useEffectiveServiceLines, isLineRuntimeEnabled } from "../hooks/useServiceLines";
import { isPreviewModeEnabled } from "../config/serviceLines";

// Service Card Component
const ServiceCard = ({ 
  title, 
  subtitle, 
  description, 
  icon: Icon, 
  accentColor, 
  bgImage, 
  href, 
  isPrimary = false,
  comingSoon = false 
}) => {
  const navigate = useNavigate();
  
  return (
    <div 
      className={`group relative overflow-hidden rounded-2xl transition-all duration-500 ${
        comingSoon ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-[1.02]'
      } ${
        isPrimary ? 'md:col-span-1 md:row-span-2' : ''
      } ${comingSoon ? 'opacity-80 grayscale' : ''}`}
      onClick={() => !comingSoon && navigate(href)}
      data-testid={`service-card-${(title || '').toLowerCase().replace(/\s+/g,'-')}`}
      aria-disabled={comingSoon}
      style={{
        background: `linear-gradient(135deg, ${accentColor}40 0%, #020617 100%)`,
        minHeight: isPrimary ? '320px' : '240px'
      }}
    >
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
        style={{ 
          backgroundImage: bgImage ? `url(${bgImage})` : 'none',
          opacity: 0.3
        }}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-[#020617]/70 to-transparent" />
      
      {/* Glow Effect on Hover */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          boxShadow: `inset 0 0 60px ${accentColor}30`
        }}
      />
      
      {/* Content */}
      <div className={`relative z-10 h-full flex flex-col justify-end ${isPrimary ? 'p-8 md:p-12' : 'p-6'}`}>
        {/* Coming Soon Badge */}
        {comingSoon && (
          <div className="absolute top-4 right-4 px-3 py-1 bg-[#D4AF37]/20 border border-[#D4AF37]/50 rounded-full">
            <span className="font-manrope text-xs text-[#D4AF37]">Coming Soon</span>
          </div>
        )}
        
        {/* Icon */}
        <div 
          className={`${isPrimary ? 'w-16 h-16' : 'w-12 h-12'} rounded-xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110`}
          style={{ 
            background: `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}80 100%)`,
            boxShadow: `0 0 30px ${accentColor}50`
          }}
        >
          <Icon className={`${isPrimary ? 'w-8 h-8' : 'w-6 h-6'} text-white`} />
        </div>
        
        {/* Text */}
        <h3 className={`font-cinzel ${isPrimary ? 'text-2xl md:text-3xl' : 'text-lg'} font-bold text-[#F8FAFC] tracking-wider mb-2`}>
          {title}
        </h3>
        <p className="font-cinzel text-sm text-[#D4AF37] tracking-wide mb-2">{subtitle}</p>
        <p className={`font-manrope ${isPrimary ? 'text-base' : 'text-sm'} text-[#94A3B8] mb-4 line-clamp-2`}>
          {description}
        </p>
        
        {/* CTA */}
        {comingSoon ? (
          <div className="flex items-center gap-2 text-[#94A3B8]">
            <span className="font-manrope text-sm">Coming Soon</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-[#D4AF37] group-hover:text-[#F8FAFC] transition-colors">
            <span className="font-manrope text-sm">Get Started</span>
            <ChevronRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
          </div>
        )}
      </div>
      
      {/* Border Glow */}
      <div 
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          border: `1px solid ${accentColor}60`
        }}
      />
    </div>
  );
};

// Event Type Pills for T-Shirt Section
const EventPill = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 px-4 py-2 bg-[#1E293B]/50 border border-[#334155] rounded-full hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/10 transition-all cursor-default">
    <Icon className="w-4 h-4 text-[#D4AF37]" />
    <span className="font-manrope text-sm text-[#F8FAFC]">{label}</span>
  </div>
);

// Reviews Slider Component
const ReviewsSlider = () => {
  const [reviews, setReviews] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const res = await fetch(`${API}/public/reviews`);
        if (res.ok) {
          const data = await res.json();
          setReviews(data);
        }
      } catch (error) {
        console.error("Error fetching reviews:", error);
      }
    };
    fetchReviews();
  }, []);

  useEffect(() => {
    if (reviews.length <= 1) return;
    
    const interval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % reviews.length);
        setIsVisible(true);
      }, 300);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [reviews.length]);

  if (reviews.length === 0) return null;

  const review = reviews[currentIndex];

  return (
    <div className={`transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      <div className="max-w-2xl mx-auto text-center">
        <div className="flex justify-center gap-1 mb-4">
          {[...Array(5)].map((_, i) => (
            <Star 
              key={i} 
              className={`w-5 h-5 ${i < review.review_rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-[#334155]'}`} 
            />
          ))}
        </div>
        <p className="font-manrope text-lg text-[#F8FAFC] italic mb-4">
          "{review.review_text}"
        </p>
        <p className="font-cinzel text-sm text-[#D4AF37] tracking-wider">
          — The {review.family_name} Family
        </p>
      </div>
    </div>
  );
};

export default function Landing() {
  const navigate = useNavigate();
  const [videoEmbedCode, setVideoEmbedCode] = useState(null);
  const videoContainerRef = useRef(null);
  const [userCountry, setUserCountry] = useState(null);
  const [headlineVariant, setHeadlineVariant] = useState(0);

  // Runtime service-line status (drives Coming Soon / Get Started / hidden per card)
  const serviceLinesConfig = useEffectiveServiceLines();
  const previewMode = isPreviewModeEnabled();
  const lineStatus = (lineId) => {
    const line = serviceLinesConfig?.lines?.find((l) => l.line_id === lineId);
    return line?.status || "active";
  };
  const cardState = (lineId) => {
    const status = lineStatus(lineId);
    // "preview" lines are hidden from the homepage unless ?preview=true (used for internal QA)
    if (status === "preview" && !previewMode) return { hidden: true };
    return { hidden: false, comingSoon: status === "disabled" };
  };

  // Select random headline variant on mount
  useEffect(() => {
    setHeadlineVariant(Math.floor(Math.random() * 5));
  }, []);

  // Detect user's country on mount (via backend to avoid CORS)
  useEffect(() => {
    const detectCountry = async () => {
      try {
        const res = await fetch(`${API}/public/geolocation`);
        if (res.ok) {
          const data = await res.json();
          if (data.country) {
            setUserCountry({
              name: data.country,
              prefix: data.prefix || ''
            });
          }
        }
      } catch (error) {
        console.log("Could not detect country:", error);
      }
    };
    detectCountry();
  }, []);

  // Headline variations based on country
  const getHeadline = () => {
    const country = userCountry?.name?.toUpperCase() || '';
    const prefix = userCountry?.prefix || '';
    
    if (!country) {
      // No country detected - show default
      return (
        <h1 className="font-cinzel text-4xl sm:text-5xl lg:text-7xl font-bold text-[#F8FAFC] tracking-wider mb-6">
          CELEBRATE IN <span className="text-[#D4AF37]">STYLE</span>
        </h1>
      );
    }

    const variants = [
      // Variant 0: Location badge style
      <h1 key="v0" className="font-cinzel text-4xl sm:text-5xl lg:text-7xl font-bold text-[#F8FAFC] tracking-wider mb-6">
        CELEBRATE IN <span className="text-[#D4AF37]">STYLE</span>
        <span className="block mt-4">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full text-lg sm:text-xl">
            <span className="text-[#94A3B8]">Serving</span>
            <span className="text-[#D4AF37]">{prefix}{country}</span>
          </span>
        </span>
      </h1>,
      
      // Variant 1: "Across" style
      <h1 key="v1" className="font-cinzel text-4xl sm:text-5xl lg:text-7xl font-bold text-[#F8FAFC] tracking-wider mb-6">
        CELEBRATE IN <span className="text-[#D4AF37]">STYLE</span>
        <span className="block text-2xl sm:text-3xl lg:text-4xl mt-2 text-[#94A3B8]">
          ACROSS {prefix}<span className="text-[#D4AF37]">{country}</span>
        </span>
      </h1>,
      
      // Variant 2: "Your Style" possessive
      <h1 key="v2" className="font-cinzel text-4xl sm:text-5xl lg:text-7xl font-bold text-[#F8FAFC] tracking-wider mb-6">
        CELEBRATE <span className="text-[#D4AF37]">YOUR</span> STYLE
        <span className="block text-2xl sm:text-3xl lg:text-4xl mt-2 text-[#94A3B8]">
          IN {prefix}<span className="text-[#D4AF37]">{country}</span>
        </span>
      </h1>,
      
      // Variant 3: Country-first integrated
      <h1 key="v3" className="font-cinzel text-4xl sm:text-5xl lg:text-7xl font-bold text-[#F8FAFC] tracking-wider mb-6">
        <span className="text-[#D4AF37]">{country}</span>
        <span className="block text-3xl sm:text-4xl lg:text-5xl mt-2">
          CELEBRATE IN <span className="text-[#D4AF37]">STYLE</span>
        </span>
      </h1>,
      
      // Variant 4: "From [Country] with Style"
      <h1 key="v4" className="font-cinzel text-4xl sm:text-5xl lg:text-7xl font-bold text-[#F8FAFC] tracking-wider mb-6">
        CELEBRATE IN <span className="text-[#D4AF37]">STYLE</span>
        <span className="block text-2xl sm:text-3xl lg:text-4xl mt-2 text-[#94A3B8]">
          FROM {prefix}<span className="text-[#D4AF37]">{country}</span> WITH LOVE
        </span>
      </h1>,
    ];

    return variants[headlineVariant];
  };

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        const res = await fetch(`${API}/public/videos/homepage`);
        if (res.ok) {
          const data = await res.json();
          setVideoEmbedCode(data.embed_code);
        }
      } catch (error) {
        console.error("Error fetching video:", error);
      }
    };
    fetchVideo();
  }, []);

  // Render embed code when it changes
  useEffect(() => {
    if (videoContainerRef.current && videoEmbedCode) {
      videoContainerRef.current.innerHTML = videoEmbedCode;
      const scripts = videoContainerRef.current.querySelectorAll('script');
      scripts.forEach(script => {
        const newScript = document.createElement('script');
        if (script.src) {
          newScript.src = script.src;
          newScript.async = true;
        } else {
          newScript.textContent = script.textContent;
        }
        script.parentNode.replaceChild(newScript, script);
      });
    }
  }, [videoEmbedCode]);

  return (
    <div className="min-h-screen bg-[#020617] overflow-hidden">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#D4AF37]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#4A0404]/20 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#022c22]/10 rounded-full blur-3xl" />
        </div>

        {/* Fireworks Animation */}
        <div className="fireworks-container">
          <div className="firework firework-1"></div>
          <div className="firework firework-2"></div>
          <div className="firework firework-3"></div>
          <div className="firework firework-4"></div>
          <div className="firework firework-5"></div>
          <div className="firework firework-6"></div>
          <div className="sparkle-trail"></div>
          <div className="sparkle-trail"></div>
          <div className="sparkle-trail"></div>
          <div className="sparkle-trail"></div>
          <div className="sparkle-trail"></div>
          <div className="sparkle-trail"></div>
          <div className="sparkle-trail"></div>
          <div className="sparkle-trail"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-12 w-full">
          {/* Main Hero Content */}
          <div className="text-center mb-16 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              <span className="font-manrope text-sm text-[#D4AF37]">AI-Powered Custom Designs</span>
            </div>
            
            {getHeadline()}
            
            <p className="font-manrope text-lg lg:text-xl text-[#94A3B8] max-w-3xl mx-auto mb-8">
              Custom designs for life's greatest moments. From celebration t-shirts to family crests, 
              we bring your milestones to life with AI-crafted precision.
            </p>

            {/* Event Type Pills */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              <EventPill icon={Cake} label="Birthdays" />
              <EventPill icon={Heart} label="Weddings" />
              <EventPill icon={PartyPopper} label="Stag & Hen" />
              <EventPill icon={GraduationCap} label="Graduations" />
              <EventPill icon={Crown} label="Milestones" />
            </div>

            {/* Video Section */}
            {videoEmbedCode && (
              <div className="max-w-3xl mx-auto mb-16">
                <div className="relative rounded-2xl overflow-hidden border-2 border-[#D4AF37]/30 bg-[#0F172A] shadow-2xl">
                  <div 
                    ref={videoContainerRef}
                    className="w-full aspect-video"
                  />
                </div>
                <p className="font-manrope text-sm text-[#94A3B8]/70 mt-3 text-center">
                  See what Happy Ages can create for you
                </p>
              </div>
            )}
          </div>

          {/* Service Cards - Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16 animate-fade-in-up stagger-2">
            {/* Celebration T-Shirts */}
            {!cardState('tshirts').hidden && (
              <ServiceCard
                title="CELEBRATION T-SHIRTS"
                subtitle="Custom Apparel"
                description="Personalised t-shirts for birthdays, anniversaries, and special occasions. Perfect for groups!"
                icon={Shirt}
                accentColor="#9f1239"
                bgImage="https://images.unsplash.com/photo-1529374255404-311a2a4f1fd9?w=800"
                href="/celebration-tshirts"
                isPrimary={true}
                comingSoon={cardState('tshirts').comingSoon}
              />
            )}
            
            {/* Coat of Arms — heritage line */}
            {!cardState('heritage').hidden && (
              <ServiceCard
                title="COAT OF ARMS"
                subtitle="Forge Your Legacy"
                description="Create a timeless family crest that embodies your heritage and values."
                icon={Shield}
                accentColor="#059669"
                bgImage="https://images.unsplash.com/photo-1569932057394-c752b271cf40?w=800"
                href="/coat-of-arms"
                comingSoon={cardState('heritage').comingSoon}
              />
            )}
            
            {/* Wanted Poster — outlaw line */}
            {!cardState('outlaw').hidden && (
              <ServiceCard
                title="WANTED POSTERS"
                subtitle="Wild West Style"
                description="Fun vintage-style wanted posters for parties and events."
                icon={Crown}
                accentColor="#78350f"
                bgImage="https://images.unsplash.com/photo-1533050487297-09b450131914?w=800"
                href="/wanted-poster"
                comingSoon={cardState('outlaw').comingSoon}
              />
            )}
            
            {/* Happy Moments — celebrations line */}
            {!cardState('celebrations').hidden && (
              <ServiceCard
                title="HAPPY MOMENTS"
                subtitle="Digital Experiences"
                description="Immersive 3D digital cards with cinematic reveals. Share unforgettable moments."
                icon={Gift}
                accentColor="#7c3aed"
                bgImage="https://images.unsplash.com/photo-1513151233558-d860c5398176?w=800"
                href="/happy-moments"
                comingSoon={cardState('celebrations').comingSoon}
              />
            )}
            
            {/* Group Cards — celebrations line */}
            {!cardState('celebrations').hidden && (
              <ServiceCard
                title="GROUP CARDS"
                subtitle="Collaborative Signing"
                description="Create beautiful group cards. Invite your team to sign with messages, GIFs & audio."
                icon={Users}
                accentColor="#10b981"
                bgImage="https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=800"
                href="/group-cards"
                comingSoon={cardState('celebrations').comingSoon}
              />
            )}

            {/* Birthday & Milestone Reminders — reminders line (Coming Soon foundation) */}
            {!cardState('reminders').hidden && (
              <ServiceCard
                title="BIRTHDAY REMINDERS"
                subtitle="Auto-Delivered Reveals"
                description="Schedule a 3D reveal to auto-deliver on the exact date — powered by our Timeless Capsule scheduler."
                icon={Cake}
                accentColor="#EC4899"
                bgImage="https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?w=800"
                href="/reminders"
                comingSoon={cardState('reminders').comingSoon}
              />
            )}
          </div>

          {/* How It Works */}
          <div className="text-center mb-16 animate-fade-in-up stagger-3">
            <h2 className="font-cinzel text-2xl sm:text-3xl font-bold text-[#F8FAFC] tracking-wider mb-12">
              HOW IT <span className="text-[#D4AF37]">WORKS</span>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { step: "01", title: "Choose Your Service", desc: "Select from our range of custom design options" },
                { step: "02", title: "Answer Questions", desc: "Tell us about your event and design preferences" },
                { step: "03", title: "Get AI Design", desc: "Our AI creates your unique, personalized design" }
              ].map((item, i) => (
                <div key={i} className="relative">
                  <div className="text-6xl font-cinzel font-bold text-[#D4AF37]/20 mb-4">{item.step}</div>
                  <h3 className="font-cinzel text-lg text-[#F8FAFC] tracking-wider mb-2">{item.title}</h3>
                  <p className="font-manrope text-sm text-[#94A3B8]">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-transparent via-[#0F172A]/50 to-transparent">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-cinzel text-2xl sm:text-3xl font-bold text-[#F8FAFC] tracking-wider text-center mb-12">
            WHAT OUR <span className="text-[#D4AF37]">CUSTOMERS SAY</span>
          </h2>
          <ReviewsSlider />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Ready to Celebrate CTA */}
          <div className="card-royal p-8 lg:p-12 relative overflow-hidden">
            {/* Background Glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#4A0404]/20 via-[#D4AF37]/10 to-[#022c22]/20" />
            
            <div className="relative z-10 text-center">
              <h2 className="font-cinzel text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-wider mb-4">
                READY TO <span className="text-[#D4AF37]">CELEBRATE?</span>
              </h2>
              <p className="font-manrope text-lg text-[#94A3B8] mb-8 max-w-2xl mx-auto">
                Start creating your custom coat of arms today. More services coming soon!
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button 
                  className="bg-[#D4AF37] text-[#020617] hover:bg-[#B5952F] font-cinzel tracking-wider px-8 py-6 text-lg"
                  onClick={() => navigate("/coat-of-arms")}
                >
                  <Shield className="w-5 h-5 mr-2" />
                  Create Coat of Arms
                </Button>
                <Button 
                  variant="outline"
                  className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 font-cinzel tracking-wider px-8 py-6 text-lg opacity-60 cursor-not-allowed"
                  disabled
                >
                  <Shirt className="w-5 h-5 mr-2" />
                  T-Shirts Coming Soon
                </Button>
              </div>
            </div>
          </div>
          
          {/* Famous Birthdays - Full width, directly below CTA */}
          <div className="mt-8">
            <FamousBirthdays />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-12 px-4 sm:px-6 border-t border-[#D4AF37]/20">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 sm:gap-6">
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            <AnimatedLogo className="w-5 h-5 sm:w-6 sm:h-6" />
            <span className="font-cinzel text-base sm:text-lg font-bold text-[#D4AF37] tracking-widest">HAPPY AGES</span>
          </Link>
          <nav className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 font-manrope text-xs sm:text-sm text-[#94A3B8]">
            <Link to="/refund-policy" className="hover:text-[#D4AF37] transition-colors" data-testid="footer-refund-link">Refunds</Link>
            <Link to="/terms" className="hover:text-[#D4AF37] transition-colors" data-testid="footer-terms-link">Terms</Link>
            <Link to="/privacy" className="hover:text-[#D4AF37] transition-colors" data-testid="footer-privacy-link">Privacy</Link>
            <a href="mailto:cheers@happyages.com" className="hover:text-[#D4AF37] transition-colors" data-testid="footer-support-link">Support</a>
          </nav>
          <p className="font-manrope text-xs sm:text-sm text-[#94A3B8]">
            © {new Date().getFullYear()} Happy Ages. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
